import React, { useRef, useEffect } from 'react';

interface QuillWrapperProps {
    value: string;
    onChange: (content: string) => void;
    modules?: any;
    formats?: string[];
    placeholder?: string;
    style?: React.CSSProperties;
}

const QuillWrapper = React.forwardRef<any, QuillWrapperProps>((props, ref) => {
    const editorRef = useRef<HTMLDivElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { value, onChange, placeholder } = props;

    useEffect(() => {
        if (editorRef.current && value !== editorRef.current.innerHTML) {
            editorRef.current.innerHTML = value || '';
        }
    }, [value]);

    const updateContent = () => {
        if (editorRef.current) {
            onChange(editorRef.current.innerHTML);
        }
    };

    const getCurrentRange = () => {
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) return null;
        return selection.getRangeAt(0);
    };

    const execCommand = (command: string, value?: string) => {
        document.execCommand(command, false, value);
        updateContent();
    };

    const insertNodeAtSelection = (node: Node) => {
        const range = getCurrentRange();
        if (!range || !editorRef.current) return;
        range.deleteContents();
        range.insertNode(node);
        range.setStartAfter(node);
        range.setEndAfter(node);
        const selection = window.getSelection();
        if (selection) {
            selection.removeAllRanges();
            selection.addRange(range);
        }
        updateContent();
    };

    const handleInsertLink = () => {
        const url = window.prompt('Entrez l\'URL du lien :');
        if (!url) return;
        execCommand('createLink', url);
    };

    const handleRemoveLink = () => {
        execCommand('unlink');
    };

    const insertInlineCode = () => {
        const range = getCurrentRange();
        if (!range || range.collapsed) return;
        const code = document.createElement('code');
        code.textContent = range.toString();
        range.deleteContents();
        range.insertNode(code);
        range.selectNode(code);
        const selection = window.getSelection();
        if (selection) {
            selection.removeAllRanges();
            selection.addRange(range);
        }
        updateContent();
    };

    const handleInsertImageClick = () => {
        fileInputRef.current?.click();
    };

    const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = () => {
            if (!reader.result) return;
            const img = document.createElement('img');
            img.src = reader.result as string;
            img.style.maxWidth = '100%';
            img.style.height = 'auto';
            insertNodeAtSelection(img);
        };
        reader.readAsDataURL(file);
        event.target.value = '';
    };

    const normalizeVideoUrl = (url: string) => {
        const youtubeMatch = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([\w-]+)/);
        if (youtubeMatch) {
            return `https://www.youtube.com/embed/${youtubeMatch[1]}`;
        }
        const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
        if (vimeoMatch) {
            return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
        }
        return url;
    };

    const handleInsertVideo = () => {
        const url = window.prompt('Entrez l\'URL de la vidéo (YouTube, Vimeo ou lien direct) :');
        if (!url) return;
        const normalizedUrl = normalizeVideoUrl(url);

        let node: HTMLElement;
        if (normalizedUrl.includes('youtube.com/embed') || normalizedUrl.includes('player.vimeo.com/video')) {
            const iframe = document.createElement('iframe');
            iframe.src = normalizedUrl;
            iframe.width = '560';
            iframe.height = '315';
            iframe.allowFullscreen = true;
            iframe.style.maxWidth = '100%';
            iframe.style.display = 'block';
            iframe.style.margin = '0 auto';
            node = iframe;
        } else {
            const video = document.createElement('video');
            video.controls = true;
            video.src = normalizedUrl;
            video.style.maxWidth = '100%';
            node = video;
        }

        insertNodeAtSelection(node);
    };

    React.useImperativeHandle(ref, () => ({
        getEditor: () => ({
            getSelection: () => {
                const selection = window.getSelection();
                if (!selection || selection.rangeCount === 0) return { index: 0 };
                const range = selection.getRangeAt(0);
                return { index: range.startOffset };
            },
            insertText: (position: number, text: string) => {
                if (editorRef.current) {
                    const selection = window.getSelection();
                    if (selection && selection.rangeCount > 0) {
                        const range = selection.getRangeAt(0);
                        range.deleteContents();
                        const textNode = document.createTextNode(text);
                        range.insertNode(textNode);
                        range.setStartAfter(textNode);
                        range.setEndAfter(textNode);
                        selection.removeAllRanges();
                        selection.addRange(range);
                        updateContent();
                    }
                }
            },
            setSelection: (position: number) => {
                editorRef.current?.focus();
            }
        })
    }));

    const handleInput = () => {
        updateContent();
    };

    return (
        <div className="quill-wrapper">
            {/* Toolbar */}
            <div className="ql-toolbar bg-gray-50 border-b border-gray-300 p-2 flex flex-wrap gap-2 items-center">
                <select
                    onChange={(e) => execCommand('fontName', e.target.value)}
                    className="border border-gray-300 rounded px-2 py-1 text-sm"
                    defaultValue=""
                >
                    <option value="">Police</option>
                    <option value="Arial">Arial</option>
                    <option value="Helvetica">Helvetica</option>
                    <option value="Georgia">Georgia</option>
                    <option value="Courier New">Courier New</option>
                    <option value="Tahoma">Tahoma</option>
                </select>

                <select
                    onChange={(e) => execCommand('fontSize', e.target.value)}
                    className="border border-gray-300 rounded px-2 py-1 text-sm"
                    defaultValue=""
                >
                    <option value="">Taille</option>
                    <option value="1">Très petit</option>
                    <option value="2">Petit</option>
                    <option value="3">Normal</option>
                    <option value="4">Grand</option>
                    <option value="5">Très grand</option>
                </select>

                <select
                    onChange={(e) => execCommand('formatBlock', e.target.value)}
                    className="border border-gray-300 rounded px-2 py-1 text-sm"
                    defaultValue=""
                >
                    <option value="">Style</option>
                    <option value="H1">Titre 1</option>
                    <option value="H2">Titre 2</option>
                    <option value="H3">Titre 3</option>
                    <option value="BLOCKQUOTE">Citation</option>
                    <option value="PRE">Code block</option>
                </select>

                <button
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); execCommand('bold'); }}
                    className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-200 font-bold"
                    title="Gras"
                >
                    B
                </button>

                <button
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); execCommand('italic'); }}
                    className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-200 italic"
                    title="Italique"
                >
                    I
                </button>

                <button
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); execCommand('underline'); }}
                    className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-200 underline"
                    title="Souligné"
                >
                    U
                </button>

                <button
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); execCommand('strikeThrough'); }}
                    className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-200"
                    title="Barré"
                >
                    S
                </button>

                <button
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); execCommand('superscript'); }}
                    className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-200"
                    title="Exposant"
                >
                    X⁺
                </button>

                <button
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); execCommand('subscript'); }}
                    className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-200"
                    title="Indice"
                >
                    X₋
                </button>

                <button
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); insertInlineCode(); }}
                    className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-200 font-mono"
                    title="Code en ligne"
                >
                    {'<>'}
                </button>

                <button
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); handleInsertLink(); }}
                    className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-200"
                    title="Insérer un lien"
                >
                    🔗
                </button>

                <button
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); handleRemoveLink(); }}
                    className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-200"
                    title="Supprimer le lien"
                >
                    ✖
                </button>

                <div className="border-l border-gray-300 h-7 mx-1"></div>

                <label className="flex items-center gap-1 border border-gray-300 rounded px-2 py-1 text-sm cursor-pointer hover:bg-gray-200">
                    <span>Couleur</span>
                    <input
                        type="color"
                        onChange={(e) => execCommand('foreColor', e.target.value)}
                        className="h-7 w-10 p-0 border-0"
                        title="Couleur du texte"
                    />
                </label>

                <label className="flex items-center gap-1 border border-gray-300 rounded px-2 py-1 text-sm cursor-pointer hover:bg-gray-200">
                    <span>Fond</span>
                    <input
                        type="color"
                        onChange={(e) => execCommand('hiliteColor', e.target.value)}
                        className="h-7 w-10 p-0 border-0"
                        title="Couleur de fond"
                    />
                </label>

                <div className="border-l border-gray-300 h-7 mx-1"></div>

                <button
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); execCommand('insertUnorderedList'); }}
                    className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-200"
                    title="Liste à puces"
                >
                    •
                </button>

                <button
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); execCommand('insertOrderedList'); }}
                    className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-200"
                    title="Liste numérotée"
                >
                    1.
                </button>

                <button
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); execCommand('indent'); }}
                    className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-200"
                    title="Augmenter le retrait"
                >
                    →
                </button>

                <button
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); execCommand('outdent'); }}
                    className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-200"
                    title="Réduire le retrait"
                >
                    ←
                </button>

                <div className="border-l border-gray-300 h-7 mx-1"></div>

                <button
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); execCommand('justifyLeft'); }}
                    className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-200"
                    title="Aligner à gauche"
                >
                    L
                </button>

                <button
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); execCommand('justifyCenter'); }}
                    className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-200"
                    title="Centrer"
                >
                    C
                </button>

                <button
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); execCommand('justifyRight'); }}
                    className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-200"
                    title="Aligner à droite"
                >
                    R
                </button>

                <button
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); execCommand('justifyFull'); }}
                    className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-200"
                    title="Justifier"
                >
                    J
                </button>

                <div className="border-l border-gray-300 h-7 mx-1"></div>

                <button
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); execCommand('direction', 'rtl'); }}
                    className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-200"
                    title="Sens du texte droite à gauche"
                >
                    RTL
                </button>

                <button
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); execCommand('direction', 'ltr'); }}
                    className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-200"
                    title="Sens du texte gauche à droite"
                >
                    LTR
                </button>

                <div className="border-l border-gray-300 h-7 mx-1"></div>

                <button
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); handleInsertImageClick(); }}
                    className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-200"
                    title="Insérer une image"
                >
                    🖼️
                </button>

                <button
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); handleInsertVideo(); }}
                    className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-200"
                    title="Insérer une vidéo"
                >
                    🎬
                </button>

                <button
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); execCommand('removeFormat'); }}
                    className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-200 text-red-600"
                    title="Nettoyer le formatage"
                >
                    ✗
                </button>
            </div>

            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                hidden
                onChange={handleImageUpload}
            />

            {/* Editor */}
            <div
                ref={editorRef}
                contentEditable
                onInput={handleInput}
                className="ql-editor p-4 min-h-[400px] max-h-[600px] overflow-y-auto focus:outline-none"
                style={{
                    fontSize: '11pt',
                    fontFamily: 'Helvetica, Arial, sans-serif',
                    lineHeight: '1.6',
                    ...props.style
                }}
                data-placeholder={placeholder}
                suppressContentEditableWarning
            />

            <style>{`
                .ql-editor[contenteditable]:empty:before {
                    content: attr(data-placeholder);
                    color: #9ca3af;
                    font-style: italic;
                }
                .ql-editor:focus {
                    outline: none;
                }
                .ql-editor h1 {
                    font-size: 16pt;
                    font-weight: bold;
                    margin: 12pt 0;
                }
                .ql-editor h2 {
                    font-size: 14pt;
                    font-weight: bold;
                    margin: 10pt 0;
                }
                .ql-editor h3 {
                    font-size: 12pt;
                    font-weight: bold;
                    margin: 8pt 0;
                }
                .ql-editor blockquote {
                    border-left: 4px solid #d1d5db;
                    margin: 0;
                    padding-left: 1rem;
                    color: #374151;
                }
                .ql-editor pre {
                    background: #f3f4f6;
                    padding: 1rem;
                    border-radius: 0.375rem;
                    overflow-x: auto;
                }
                .ql-editor code {
                    background: #f3f4f6;
                    padding: 0.15rem 0.3rem;
                    border-radius: 0.25rem;
                    font-family: 'Courier New', monospace;
                }
                .ql-editor p {
                    margin: 5pt 0;
                }
                .ql-editor strong {
                    font-weight: bold;
                }
                .ql-editor em {
                    font-style: italic;
                }
                .ql-editor u {
                    text-decoration: underline;
                }
                .ql-editor ul, .ql-editor ol {
                    margin-left: 20px;
                    margin-top: 5pt;
                    margin-bottom: 5pt;
                }
            `}</style>
        </div>
    );
});

QuillWrapper.displayName = 'QuillWrapper';

export default QuillWrapper;
